import * as React from 'react';
import { connect } from 'react-redux';
import { GradebookSelect } from './gradebookSelect/GradebookSelect';
import { I18n } from 'react-redux-i18n-lite';
import PropTypes from 'prop-types';
import * as classroomApi from '../../../services/api/classroomApi';
import * as analyticsApi from '../../../services/api/analyticsApi';
import { ANALYTICS_CONTEXT, ANALYTICS_EVENT } from '../../../types/analytics';
import RadioContainer from '../../../components/library/RadioContainer';
import FileDownloaderService from '../../../services/stream/FileDownloadService';
import GradebookOptions from './GradebookOptions';
import { Selector } from '@netexknowledge/smart-components';
import { GRADEBOOK_SCOPE, TASK_TABS_CONSTANTS } from '../../../const';
import { Fragment } from 'react';
import { isMobileViewport } from '../../../utils/mobileUtils';

export class GradebookSidebar extends React.Component {
  static propTypes = {
    routeParams: PropTypes.any,
    books: PropTypes.array,
    units: PropTypes.array,
    lessons: PropTypes.array,
    selectedBook: PropTypes.object,
    selectedUnit: PropTypes.object,
    selectedLesson: PropTypes.object,
    setBookData: PropTypes.func,
    setUnitData: PropTypes.func,
    setLessonData: PropTypes.func,
    setSelectedBook: PropTypes.func,
    setSelectedUnit: PropTypes.func,
    setSelectedLesson: PropTypes.func,
    loadTasks: PropTypes.func,
    colors: PropTypes.any,
    sectionId: PropTypes.string,
    tasksFilterParams: PropTypes.object,
    changeTasksFilterParams: PropTypes.func,
    classroomTabs: PropTypes.array
  };

  constructor(props) {
    super(props);

    const tasksTabs = {
      ...TASK_TABS_CONSTANTS,
      ALL: {
        key: 'ALL',
        label: 'tasks.all'
      }
    };
    delete tasksTabs.FUTURE;

    this.state = {
      exportingNotes: false,
      unitAndLessonsScopeDisabled: false,
      tasksTabs
    };
  }

  componentDidUpdate(prevProps) {
    const { books, setBookData, routeParams, sectionId, selectedBook } = this.props;

    if (prevProps.books !== books && books.length === 1 && !selectedBook) {
      setBookData(routeParams.idClassroom, books[0], sectionId);
    }

    if (this.props.selectedLesson && prevProps.selectedLesson !== this.props.selectedLesson) {
      this.setState({ unitAndLessonsScopeDisabled: true });
    } else if (this.state.unitAndLessonsScopeDisabled && !this.props.selectedLesson) {
      this.setState({ unitAndLessonsScopeDisabled: false });
    }
  }

  componentWillUnmount() {
    const { setSelectedBook, setSelectedUnit, setSelectedLesson } = this.props;

    // Swapped for the mobile filter bar (phone rotated): the selection stays
    if (isMobileViewport()) return;

    setSelectedBook(null);
    setSelectedUnit(null);
    setSelectedLesson(null);
  }

  exportBookToCSV = () => {
    const { selectedBook, sectionId } = this.props;

    if (!this.state.exportingNotes) {
      this.setState({ exportingNotes: true });
      const startedAt = Date.now();

      classroomApi
        .getBookCsv(sectionId, selectedBook.activityId)
        .then(dataStream => {
          FileDownloaderService.downloadFromRawBinaryStream(
            dataStream.data,
            `${I18n.t('leftMenu.gradebook')} ${this.getBookName()}`,
            '.csv'
          );
          this.setState({ exportingNotes: false });
          this.trackGradesExport({ status: 'success', startedAt });
        })
        .catch(err => {
          console.error('error getting the book csv %s', err);
          this.setState({ exportingNotes: false });
          this.trackGradesExport({ status: 'error', startedAt, errorMessage: err && err.message });
        });
    }
  };

  trackGradesExport = ({ status, startedAt, errorMessage }) => {
    const { selectedBook, selectedUnit, routeParams } = this.props;

    analyticsApi.trackAnalyticsEvent({
      event: ANALYTICS_EVENT.GRADES_EXPORT_CSV,
      classId: routeParams.idClassroom,
      bookId: selectedBook.activityId,
      unitId: selectedUnit ? selectedUnit.activityId || selectedUnit.id : null,
      context: ANALYTICS_CONTEXT.GRADES_EXPORT_BUTTON,
      status,
      durationMs: Date.now() - startedAt,
      ...(errorMessage && { errorMessage })
    });
  };

  getBookName = () => {
    const { selectedBook } = this.props;
    return typeof selectedBook.name === 'object' ? Object.values(selectedBook.name)[0] : selectedBook.name;
  };

  isBookSelected = () => {
    return this.props.selectedBook && !this.props.selectedUnit && !this.props.selectedLesson;
  };

  isUnitSelected = () => {
    return this.props.selectedBook && this.props.selectedUnit && !this.props.selectedLesson;
  };

  isLessonSelected = () => {
    return this.props.selectedBook && this.props.selectedUnit && this.props.selectedLesson;
  };

  isTasksScope = () => this.props.gradebookScope === GRADEBOOK_SCOPE.TASKS;
  isContentScope = () => [GRADEBOOK_SCOPE.ACTIVITIES, GRADEBOOK_SCOPE.UNIT_LESSONS].includes(this.props.gradebookScope);

  getTasksFilterSelectOptions = () => {
    const { tasksTabs } = this.state;
    const mapped = Object.keys(tasksTabs)
      .map(key => {
        const option = { value: key };
        option.name = I18n.t(tasksTabs[key].label);
        return option;
      })
      .map(item => ({ [item.value]: item.name }));

    return Object.assign({}, ...mapped);
  };

  handleTasksFilterChange = filterParams => {
    const { routeParams, changeTasksFilterParams } = this.props;
    const { tasksTabs } = this.state;

    if (filterParams.value === 'ALL') {
      changeTasksFilterParams(routeParams.idClassroom, {});
    } else {
      const tabSelected = tasksTabs[filterParams.value];
      changeTasksFilterParams(routeParams.idClassroom, { status: tabSelected });
    }
  };

  getUnitLessonScopeText = () => {
    let translationKey;
    if (this.isBookSelected()) {
      translationKey = 'gradebook.scope.units';
    } else if (this.isUnitSelected()) {
      translationKey = 'gradebook.scope.lessons';
    } else {
      translationKey = 'gradebook.scope.unit_lessons';
    }
    return I18n.t(translationKey);
  };

  render() {
    const {
      sectionId,
      colors,
      routeParams,
      books,
      units,
      lessons,
      setBookData,
      setUnitData,
      setGradebookScope,
      setLessonData,
      selectedBook,
      selectedUnit,
      selectedLesson,
      gradebookScope,
      tasksFilterParams,
      classroomTabs
    } = this.props;
    const { tasksTabs } = this.state;
    const hasTasksScope = (classroomTabs || []).includes('ASSIGNMENTS');
    return (
      <div className="side-bar">
        {hasTasksScope && (
        <Fragment>
          <div className="gradebook-sidebar-title">{I18n.t('common.option.view')}</div>
          <div className="gradebook-sidebar-radio-scope">
              <RadioContainer
                checkId={I18n.t('gradebook.scope.tasks')}
                checked={this.isTasksScope()}
                onCheck={() => {
                  setGradebookScope(GRADEBOOK_SCOPE.TASKS);
                } }
                classes={{ label: 'gradebook-sidebar-radio-text' }} />
              <RadioContainer
                checkId={I18n.t('gradebook.scope.content')}
                checked={this.isContentScope()}
                onCheck={() => setGradebookScope(GRADEBOOK_SCOPE.ACTIVITIES)}
                classes={{ label: 'gradebook-sidebar-radio-text' }} />
            </div>
          </Fragment>
        )}
        {this.isTasksScope() == false ? (
          <Fragment>
            <div className="gradebook-sidebar-title">{I18n.t('gradebook.select.title')}</div>

            <GradebookSelect
              sectionId={sectionId}
              colors={colors}
              classroomId={routeParams.idClassroom}
              label={I18n.t('gradebook.select.books')}
              options={books}
              setSelectedItem={setBookData}
              selectedItem={selectedBook}
            />

            <GradebookSelect
              sectionId={sectionId}
              colors={colors}
              classroomId={routeParams.idClassroom}
              label={I18n.t('gradebook.select.units')}
              options={units}
              setSelectedItem={unit => setUnitData(unit, sectionId, selectedBook, routeParams.idClassroom)}
              selectedItem={selectedUnit}
            />

            <GradebookSelect
              sectionId={sectionId}
              colors={colors}
              classroomId={routeParams.idClassroom}
              label={I18n.t('gradebook.select.lessons')}
              options={lessons}
              setSelectedItem={lesson => setLessonData(lesson, sectionId, selectedUnit, routeParams.idClassroom)}
              selectedItem={selectedLesson}
            />

            <div className="gradebook-sidebar-title">{I18n.t('gradebook.scope.title')}</div>

            <div className="gradebook-sidebar-radio-scope">
              <RadioContainer
                checkId={I18n.t('gradebook.scope.activities')}
                checked={gradebookScope === GRADEBOOK_SCOPE.ACTIVITIES}
                onCheck={() => setGradebookScope(GRADEBOOK_SCOPE.ACTIVITIES)}
                classes={{ label: 'gradebook-sidebar-radio-text' }}
              />

              {!this.isLessonSelected() && (
                <RadioContainer
                  checkId={this.getUnitLessonScopeText()}
                  checked={gradebookScope === GRADEBOOK_SCOPE.UNIT_LESSONS}
                  disabled={this.state.unitAndLessonsScopeDisabled}
                  onCheck={() => setGradebookScope(GRADEBOOK_SCOPE.UNIT_LESSONS)}
                  classes={{ label: 'gradebook-sidebar-radio-text' }}
                />
              )}
            </div>

            <hr className="grades-sidebar-separator" />
            {this.isUnitSelected() && (
              <GradebookOptions onClick={this.exportBookToCSV} exportingNotes={this.state.exportingNotes} />
            )}
          </Fragment>
        ) : (
          <Fragment>
            <div className="gradebook-sidebar-title">{I18n.t('common.appliedFilters')}</div>

            <div className="selector-container">
              <Selector
                id="gradebook-task-status-filter"
                elements={this.getTasksFilterSelectOptions()}
                onChange={e => this.handleTasksFilterChange({ value: e, name: I18n.t(tasksTabs[e].label) })}
                selectedKey={tasksFilterParams?.status?.key || tasksTabs.IN_PROGRESS.key}
              />
            </div>
          </Fragment>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  classroomTabs: state.account.configuration.classroomTabs
});

export default connect(mapStateToProps)(GradebookSidebar);
